import { WorkflowChatTransport } from "@ai-sdk/workflow/client";
import type { MagnusUIMessage } from "@magnus/contract/messages";
import {
  type ClientTtftRecord,
  isModelOutputChunkType,
  markPhase,
  TTFT_PHASE,
  type TtftPhases,
} from "@magnus/contract/ttft";
import type { ChatTransport, UIMessageChunk } from "ai";
import {
  acquireReaderSlot,
  MAX_CONSECUTIVE_RECONNECT_ERRORS,
  type ReaderConflict,
  reconnectIterator,
  sanitizeChunkStream,
  withLeaseRelease,
} from "../chat-client";

export interface CreateChatTransportOptions {
  /** Origin, e.g. "" (relative) or "https://magnus.example.com". `/api/chat` is appended. */
  baseUrl: string;
  /**
   * Chat id this transport drives. Used to enforce a single active stream
   * reader per chat (live send vs. resume) so concurrent readers can't
   * double-feed the shared `useChat` store and jumble the rendered reply.
   */
  chatId: string;
  /** Clear the persisted run id once a stream ends. */
  clearRunId: () => void;
  /** Auth-applied fetch (see `createMagnusFetch`). */
  fetch: typeof globalThis.fetch;
  /** Per-send request body (model, workspace, capabilities, surface, ...). Merged with `{ message, chatId }`. */
  getBody: () => Record<string, unknown>;
  /** Resolve the workflow run id to resume against, or `null` when nothing is resumable. */
  getRunId: () => string | null;
  /**
   * Optional TTFT (time-to-first-token) observability hook. Fired once per
   * send — when the first `text-delta` chunk is decoded, or at stream end if
   * the turn produced no text — with the client-side phase marks (send start,
   * response headers, first chunk, first model output, first text delta) and
   * the run id. The SDK stays telemetry-sink-free — the host decides where
   * these land (e.g. the web app beacons them to `/api/chat/ttft-report`). Not
   * fired on the resume/reconnect path (TTFT is a first-send concern).
   */
  onClientTtft?: (record: ClientTtftRecord) => void;
  /** Observability hook fired when a concurrent reader is suppressed/aborted. */
  onReaderConflict?: (conflict: ReaderConflict) => void;
  /** Fired when the resume replay boundary is reached (used to gate smooth-typing). */
  onResumeReplayComplete?: () => void;
  /** Persist the workflow run id once a send returns it (header `x-workflow-run-id`). */
  setRunId: (runId: string) => void;
}

// Some AI SDK versions pass an `abortSignal` to `reconnectToStream` at
// runtime even though the published type omits it; the current one does NOT
// (it calls with only `{chatId, metadata, headers, body}`, before the
// per-request abort controller even exists). Keep the widening so the signal
// is threaded through whenever a version does provide it — but nothing may
// RELY on it: user stop on a resumed stream goes through the reader
// registry's `abortActiveReader` instead (see `useMagnus.handleStop`).
type ReconnectOptions = Parameters<
  ChatTransport<MagnusUIMessage>["reconnectToStream"]
>[0] & { abortSignal?: AbortSignal };

function iteratorToStream<T>(
  iterator: AsyncIterator<T>,
  signal?: AbortSignal
): ReadableStream<T> {
  return new ReadableStream<T>({
    async pull(controller) {
      if (signal?.aborted) {
        controller.error(signal.reason);
        return;
      }
      const { done, value } = await iterator.next();
      if (done) {
        controller.close();
        return;
      }
      controller.enqueue(value);
    },
    async cancel(reason) {
      await iterator.return?.(reason);
    },
  });
}

async function* streamToIterator<T>(
  stream: ReadableStream<T>
): AsyncGenerator<T, void, void> {
  const reader = stream.getReader();
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        return;
      }
      yield value;
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Run a chunk stream through `sanitizeChunkStream` so a turn whose head chunks
 * were lost in transit (a mid-turn reconnect replaying past `text-start`, a
 * dropped stream-store write, a client/server cursor off-by-one) degrades to a
 * small content gap instead of the AI SDK hard-erroring the whole turn with
 * "Received text-delta for missing text part". No-op on well-formed streams.
 * Mirrors the mobile transport (apps/mobile/lib/chat-transport.ts).
 */
function withSanitizedChunks(
  stream: ReadableStream<UIMessageChunk>,
  signal?: AbortSignal
): ReadableStream<UIMessageChunk> {
  // `dropOrphanedToolChunks` is safe here (and only here): a fresh send
  // always starts the assistant message from an empty parts array, so a tool
  // output/approval chunk whose input chunk never arrived can only ever
  // hard-error the turn. The reconnect path below must NOT drop — a resumed
  // client may already hold the tool part from before the disconnect.
  return iteratorToStream(
    sanitizeChunkStream(streamToIterator(stream), {
      dropOrphanedToolChunks: true,
    }),
    signal
  );
}

/**
 * Tap a client-facing chunk stream to stamp the first-chunk, first-model-output,
 * and first-text-delta TTFT marks (epoch ms, browser clock). Passthrough —
 * never alters chunks or timing. Fires `onReport` once: at the first text
 * delta, or at stream end for turns that never produce text (tool-only,
 * reasoning-only, errored), so those still report their first-output mark.
 */
function tapClientTtft(
  stream: ReadableStream<UIMessageChunk>,
  phases: TtftPhases,
  onReport: () => void
): ReadableStream<UIMessageChunk> {
  let sawChunk = false;
  let sawOutput = false;
  let reported = false;
  const report = () => {
    if (reported) {
      return;
    }
    reported = true;
    // Telemetry must never break the chat: a throwing host sink here would
    // otherwise error the TransformStream and kill the live turn mid-stream.
    // Swallow it — this is a passthrough tap.
    try {
      onReport();
    } catch {
      // Best-effort telemetry only.
    }
  };
  return stream.pipeThrough(
    new TransformStream<UIMessageChunk, UIMessageChunk>({
      transform(chunk, controller) {
        if (!sawChunk) {
          sawChunk = true;
          markPhase(phases, TTFT_PHASE.clientFirstChunk);
        }
        if (!sawOutput && isModelOutputChunkType(chunk.type)) {
          sawOutput = true;
          markPhase(phases, TTFT_PHASE.clientFirstModelOutput);
        }
        if (!reported && chunk.type === "text-delta") {
          markPhase(phases, TTFT_PHASE.clientFirstTextDelta);
          report();
        }
        controller.enqueue(chunk);
      },
      flush() {
        report();
      },
    })
  );
}

/**
 * Build the AI SDK `ChatTransport` that talks to Magnus's `/api/chat` (SSE)
 * and resumes via `/api/chat/{runId}/stream`. Framework-agnostic: the
 * caller supplies run-id storage + body getters; `@magnus/react`'s
 * `useCopilot` wires these to React state.
 *
 * This generalizes the previously app-local transport in
 * `apps/web/hooks/use-chat-agent.ts` by removing the hardcoded
 * `/api/chat` base and the Office-specific fetch.
 */
export function createChatTransport(
  options: CreateChatTransportOptions
): ChatTransport<MagnusUIMessage> {
  const chatApi = `${options.baseUrl}/api/chat`;

  // Per-send TTFT capture state. Sends are serialized per chat (the reader
  // lease aborts a prior in-flight reader on a new send), and this transport
  // is per chat, so a single active record is sufficient. `onChatSendMessage`
  // (fired once with the response headers) stamps the response mark + run id
  // onto whichever send is currently active.
  let activeSend: { phases: TtftPhases; runId: string | null } | null = null;

  const inner = new WorkflowChatTransport({
    api: chatApi,
    fetch: options.fetch,
    maxConsecutiveErrors: MAX_CONSECUTIVE_RECONNECT_ERRORS,
    prepareSendMessagesRequest: ({ messages, id, body }) => {
      // Single authoritative `getBody()` call for this send. `getBody` may be
      // side-effecting in hosts (the web app regenerates `clientSentAt` and
      // consumes a one-shot voice flag here), so it must be invoked exactly
      // once — capture `clientSentAt` off the built body rather than calling
      // `getBody()` again for TTFT.
      const extra = options.getBody();
      if (activeSend && typeof extra.clientSentAt === "number") {
        markPhase(
          activeSend.phases,
          TTFT_PHASE.clientSentAt,
          extra.clientSentAt
        );
      }
      return {
        body: {
          ...extra,
          // AI SDK per-request options belong to this exact send, including
          // sends waiting behind a stop. Never consume shared one-shot refs.
          ...body,
          message: messages.at(-1),
          chatId: id,
        },
      };
    },
    onChatSendMessage: (response) => {
      const runId = response.headers.get("x-workflow-run-id");
      if (runId) {
        options.setRunId(runId);
      }
      if (activeSend) {
        markPhase(activeSend.phases, TTFT_PHASE.clientResponse);
        activeSend.runId = runId;
      }
    },
    onChatEnd: () => {
      options.clearRunId();
    },
  });

  return {
    sendMessages: async (sendOptions) => {
      // Open a TTFT record for this send: stamp the fetch-dispatch mark now.
      // `clientSentAt` is stamped in `prepareSendMessagesRequest` (off the one
      // authoritative `getBody()` build), and the response + first-chunk /
      // first-text marks are stamped later (headers callback and stream tap).
      const phases: TtftPhases = {};
      markPhase(phases, TTFT_PHASE.clientSendStart);
      const sendRecord = { phases, runId: null as string | null };
      activeSend = sendRecord;
      const emitTtft = () =>
        options.onClientTtft?.({
          chatId: options.chatId,
          runId: sendRecord.runId,
          phases,
        });

      // A send is authoritative: it takes over the chat's single reader slot,
      // aborting any in-flight resume so the two can't both write the store.
      const lease = acquireReaderSlot({
        chatId: options.chatId,
        mode: "send",
        upstreamSignal: sendOptions.abortSignal,
        onConflict: options.onReaderConflict,
      });
      if (!lease) {
        // Unreachable for `send` (always acquires), but keep the type honest.
        return tapClientTtft(
          withSanitizedChunks(
            await inner.sendMessages(sendOptions),
            sendOptions.abortSignal
          ),
          phases,
          emitTtft
        );
      }
      try {
        const upstream = await inner.sendMessages({
          ...sendOptions,
          abortSignal: lease.signal,
        });
        return withLeaseRelease(
          tapClientTtft(
            withSanitizedChunks(upstream, lease.signal),
            phases,
            emitTtft
          ),
          lease.release
        );
      } catch (err) {
        lease.release();
        throw err;
      }
    },
    reconnectToStream: (reconnectOptions) => {
      const runId = options.getRunId();
      if (runId === null) {
        return Promise.resolve(null);
      }
      const opts = reconnectOptions as ReconnectOptions;
      // Suppress a resume when a reader is already active for this chat (a live
      // send, or an earlier resume): a second consumer of the same run replays
      // the same chunks into the shared store and jumbles the reply.
      const lease = acquireReaderSlot({
        chatId: options.chatId,
        mode: "resume",
        upstreamSignal: opts.abortSignal,
        onConflict: options.onReaderConflict,
      });
      if (!lease) {
        return Promise.resolve(null);
      }
      const iterator = sanitizeChunkStream(
        reconnectIterator({
          runId,
          replayFromStart: true,
          baseUrl: chatApi,
          fetch: options.fetch,
          abortSignal: lease.signal,
          onResumeReplayComplete: options.onResumeReplayComplete,
          onCleanFinish: options.clearRunId,
        })
      );
      return Promise.resolve(
        withLeaseRelease(
          iteratorToStream(iterator, lease.signal),
          lease.release
        )
      );
    },
  };
}
